import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  endSupportSession,
  fetchMySupportSession,
  isSupportSessionActive,
  startSupportSession,
  SAFE_SUPPORT_SESSION_END_FAILURE,
  type StartSupportSessionInput,
  type SupportSession,
} from "../data/support-session";

// Console-wide state for "act as this member" (product lock 2026-08-04). One provider sits above
// the router so the banner, the workspace pin, and the permission refresh all read the same
// session — there is no second source of truth in the tree.
//
// RPC discipline on the hidden path: exactly ONE my_platform_support_session() read per console
// load, and polling ONLY while a session is actually open. A console user who can never start a
// session (everyone but a platform superadmin) therefore costs one cheap read and nothing more.
// That single read is not optional: the banner has to survive a refresh, and the console cannot
// know it is mid-session before it asks.
//
// Nothing here decides anything. The server owns who may start, what the actor may then see, and
// when the session dies; this provider only keeps the chrome honest about it.

export type StartSupportOutcome = { ok: true } | { ok: false; reason: string };

export interface SupportSessionState {
  /** The open session, or null. Never a session whose clock has already run out. */
  session: SupportSession | null;
  /** True until the first read settles — the shell holds the banner rather than flashing it. */
  loading: boolean;
  start: (input: StartSupportSessionInput) => Promise<StartSupportOutcome>;
  end: () => Promise<StartSupportOutcome>;
  refresh: () => void;
}

/**
 * Inert default so any surface can call useSupportSession() outside a provider (unit tests,
 * isolated renders) without exploding — and, more importantly, without ever appearing to hold a
 * session it does not have. start/end refuse rather than pretending.
 */
const INERT: SupportSessionState = {
  session: null,
  loading: false,
  start: async () => ({ ok: false, reason: SAFE_SUPPORT_SESSION_END_FAILURE }),
  end: async () => ({ ok: false, reason: SAFE_SUPPORT_SESSION_END_FAILURE }),
  refresh: () => {},
};

const SupportSessionContext = createContext<SupportSessionState>(INERT);

export function useSupportSession(): SupportSessionState {
  return useContext(SupportSessionContext);
}

/** Poll cadence while a session is open — the server may end or expire it out from under us. */
const ACTIVE_POLL_MS = 30000;

export function SupportSessionProvider({
  client,
  children,
  pollMs = ACTIVE_POLL_MS,
}: {
  client: SupabaseClient;
  children: ReactNode;
  pollMs?: number;
}) {
  const [session, setSession] = useState<SupportSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  const read = useCallback(async (): Promise<SupportSession | null> => {
    const next = await fetchMySupportSession(client);
    if (mounted.current) {
      setSession(next);
      setLoading(false);
    }
    return next;
  }, [client]);

  // Initial read + re-read on refresh(). Never throws: fetchMySupportSession fails closed to null.
  useEffect(() => {
    let active = true;
    void (async () => {
      const next = await fetchMySupportSession(client);
      if (!active) return;
      setSession(next);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [client, nonce]);

  // Poll ONLY while open, so a server-side end/expire/replace drops the banner on its own.
  const sessionId = session?.sessionId ?? null;
  useEffect(() => {
    if (sessionId === null || pollMs <= 0) {
      return;
    }
    const timer = setInterval(() => void read(), pollMs);
    return () => clearInterval(timer);
  }, [sessionId, pollMs, read]);

  // Local expiry: drop the banner the moment the clock runs out rather than waiting for the next
  // poll. The server already treats the session as over; this stops the UI claiming otherwise.
  const expiresAt = session?.expiresAt ?? null;
  useEffect(() => {
    if (expiresAt === null) {
      return;
    }
    const remainingMs = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(remainingMs)) {
      return;
    }
    const timer = setTimeout(() => {
      if (mounted.current) {
        setSession(null);
      }
      refresh();
    }, Math.max(0, remainingMs));
    return () => clearTimeout(timer);
  }, [expiresAt, refresh]);

  const start = useCallback(
    async (input: StartSupportSessionInput): Promise<StartSupportOutcome> => {
      const outcome = await startSupportSession(client, input);
      if (!outcome.ok) {
        return outcome;
      }
      // Re-read rather than trusting the start projection for banner copy: the safe projection
      // may omit the display name / account name, and inventing either would be a lie in chrome
      // that is supposed to be the most trustworthy thing on the screen.
      const hydrated = await read();
      if (hydrated === null && isSupportSessionActive(outcome.session)) {
        if (mounted.current) {
          setSession(outcome.session);
        }
      }
      return { ok: true };
    },
    [client, read],
  );

  const end = useCallback(async (): Promise<StartSupportOutcome> => {
    const outcome = await endSupportSession(client);
    if (!outcome.ok) {
      // Do NOT clear the banner on a failed end — the session is still open server-side and the
      // operator needs to know they are still acting as someone else.
      return outcome;
    }
    if (mounted.current) {
      setSession(null);
    }
    refresh();
    return { ok: true };
  }, [client, refresh]);

  const value = useMemo<SupportSessionState>(
    () => ({ session, loading, start, end, refresh }),
    [session, loading, start, end, refresh],
  );

  return <SupportSessionContext.Provider value={value}>{children}</SupportSessionContext.Provider>;
}
